import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Container,
    Paper,
    Group,
    Stack,
    Title,
    Text,
    Tabs,
    Button,
    Table,
    Badge,
    ActionIcon,
    Modal,
    TextInput,
    Select,
    NumberInput,
    Tooltip,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconPlus, IconPencil, IconTrash } from "@tabler/icons-react";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";
import { getAuthHeaders } from "../../../api/authHelper";

const RED = "#ff5e5e";
const apiUrl = process.env.REACT_APP_API_URL;

type AdminEventDto = {
    eventId: number;
    name: string;
};

type MeetingDto = {
    meetingId: number;
    meetingDate?: string;
    meetingStart?: string;
    meetingSlut?: string;
    meetingName?: string;
    kontaktPerson?: string;
    kontaktPersonMobile?: string;
    kontaktPersonEmail?: string;
    antalPersoner?: number;
    bordOpstilling?: number | null;
    udstyr?: number[] | null;
    meetingPakkeId?: number | null;
    createdDate?: string;
    eventId?: number;
    createdByUserId?: number;
};

type OptionDto = {
    value: number;
    text: string;
};

export default function MeetingAdminPage() {
    const [events, setEvents] = useState<AdminEventDto[]>([]);
    const [eventId, setEventId] = useState<number | null>(null);

    const [bookings, setBookings] = useState<MeetingDto[]>([]);
    const [pakker, setPakker] = useState<OptionDto[]>([]);
    const [udstyr, setUdstyr] = useState<OptionDto[]>([]);
    const [bordopstillinger, setBordopstillinger] = useState<OptionDto[]>([]);

    const [, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<string | null>("bookings");

    // Booking modal
    const [bookingModalOpen, setBookingModalOpen] = useState(false);
    const [bookingEditing, setBookingEditing] = useState<MeetingDto | null>(null);

    const [meetingDate, setMeetingDate] = useState("");
    const [meetingStart, setMeetingStart] = useState("");
    const [meetingSlut, setMeetingSlut] = useState("");
    const [meetingName, setMeetingName] = useState("");
    const [kontaktPerson, setKontaktPerson] = useState("");
    const [kontaktPersonMobile, setKontaktPersonMobile] = useState("");
    const [kontaktPersonEmail, setKontaktPersonEmail] = useState("");
    const [antalPersoner, setAntalPersoner] = useState(0);
    const [meetingPakkeId, setMeetingPakkeId] = useState<number | null>(null);
    const [bordOpstilling, setBordOpstilling] = useState<number | null>(null);
    const [bookingUdstyr, setBookingUdstyr] = useState("");

    // Option modal
    const [optionModalOpen, setOptionModalOpen] = useState(false);
    const [optionType, setOptionType] = useState<"pakker" | "udstyr" | "bord">("pakker");
    const [optionEditing, setOptionEditing] = useState<OptionDto | null>(null);
    const [optionValue, setOptionValue] = useState<number>(0);
    const [optionText, setOptionText] = useState("");



    useEffect(() => {
        (async () => {
            const res = await axios.get(`${apiUrl}/api/admin/meetings`, {
                params: { eventId },
                headers: getAuthHeaders() as Record<string, string>,
            });
            const res1 = await axios.get(`${apiUrl}/api/meeting/admin/events`, {
                headers: getAuthHeaders() as Record<string, string>,
            });
            setEvents(res1.data);
            if (res1.data?.length) {
                setEventId(res1.data[0].eventId);
            }
        })();
    }, []);

    const eventOptions = useMemo(() => {
        const unique = events.filter(
            (event, index, self) =>
                index === self.findIndex((e) => e.eventId === event.eventId)
        );

        return unique.map((e) => ({
            value: String(e.eventId),
            label: e.name,
        }));
    }, [events]);

    const pakkeOptions = useMemo(
        () => pakker.map((p) => ({ value: String(p.value), label: p.text })),
        [pakker]
    );

    const bordOptions = useMemo(
        () => bordopstillinger.map((b) => ({ value: String(b.value), label: b.text })),
        [bordopstillinger]
    );

    const pakkeMap = useMemo(
        () => Object.fromEntries(pakker.map((x) => [x.value, x.text])),
        [pakker]
    );

    const bordMap = useMemo(
        () => Object.fromEntries(bordopstillinger.map((x) => [x.value, x.text])),
        [bordopstillinger]
    );

    const udstyrMap = useMemo(
        () => Object.fromEntries(udstyr.map((x) => [x.value, x.text])),
        [udstyr]
    );

    const refreshAll = async (eid: number) => {
        setLoading(true);
        try {
            const bookingRes = await axios.get(`${apiUrl}/api/admin/meetings`, {
                params: { eventId: eid },
                headers: getAuthHeaders() as Record<string, string>,
            });

            const headers = {
                headers: getAuthHeaders() as Record<string, string>,
            };

            const [pakkeRes, udstyrRes, bordRes] = await Promise.all([
                axios.get(`${apiUrl}/api/Option_MeetingPakke`, headers),
                axios.get(`${apiUrl}/api/Option_MeetingUdstyr`, headers),
                axios.get(`${apiUrl}/api/Option_MeetingBordopstilling`, headers),
            ]);

            setBookings(bookingRes.data);
            setPakker(pakkeRes.data);
            setUdstyr(udstyrRes.data);
            setBordopstillinger(bordRes.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (eventId) {
            refreshAll(eventId);
        }
    }, [eventId]);

    const onTabChange = async (tab: string | null) => {
        setActiveTab(tab);
        if (eventId) {
            await refreshAll(eventId);
        }
    };

    // --------------------
    // Booking
    // --------------------
    const resetBookingForm = () => {
        setBookingEditing(null);
        setMeetingDate("");
        setMeetingStart("");
        setMeetingSlut("");
        setMeetingName("");
        setKontaktPerson("");
        setKontaktPersonMobile("");
        setKontaktPersonEmail("");
        setAntalPersoner(0);
        setMeetingPakkeId(null);
        setBordOpstilling(null);
        setBookingUdstyr("");
    };

    const openCreateBooking = () => {
        resetBookingForm();
        setBookingModalOpen(true);
    };

    const openEditBooking = (b: MeetingDto) => {
        setBookingEditing(b);
        setMeetingDate(b.meetingDate ?? "");
        setMeetingStart(b.meetingStart ?? "");
        setMeetingSlut(b.meetingSlut ?? "");
        setMeetingName(b.meetingName ?? "");
        setKontaktPerson(b.kontaktPerson ?? "");
        setKontaktPersonMobile(b.kontaktPersonMobile ?? "");
        setKontaktPersonEmail(b.kontaktPersonEmail ?? "");
        setAntalPersoner(b.antalPersoner ?? 0);
        setMeetingPakkeId(b.meetingPakkeId ?? null);
        setBordOpstilling(b.bordOpstilling ?? null);
        setBookingUdstyr(
            b.udstyr ? JSON.stringify(b.udstyr) : ""
        );
        setBookingModalOpen(true);
    };

    const saveBooking = async () => {
        if (!eventId) return;

        if (!meetingName.trim()) {
            showNotification({ title: "Mangler", message: "Mødenavn er påkrævet" });
            return;
        }

        const payload = {
            meetingId: bookingEditing?.meetingId,
            meetingDate,
            meetingStart,
            meetingSlut,
            meetingName,
            kontaktPerson,
            kontaktPersonMobile,
            kontaktPersonEmail,
            antalPersoner,
            bordOpstilling,
            udstyr: bookingUdstyr,
            meetingPakkeId,
            eventId,
            createdByUserId: 1,
        };

        if (!bookingEditing) {
            await axios.post(`${apiUrl}/api/FormMeeting`, payload);
            showNotification({ title: "Gemt", message: "Booking oprettet" });
        } else {
            await await axios.put(`${apiUrl}/api/admin/meetings/${bookingEditing.meetingId}`, payload);
            showNotification({ title: "Gemt", message: "Booking opdateret" });
        }

        setBookingModalOpen(false);
        await refreshAll(eventId);
    };

    const deleteBooking = async (id: number) => {
        if (!eventId) return;

        await axios.delete(`${apiUrl}/api/admin/meetings/${id}`);
        showNotification({ title: "OK", message: "Booking slettet" });
        await refreshAll(eventId);
    };

    // --------------------
    // Options
    // --------------------
    const getOptionUrl = (type: "pakker" | "udstyr" | "bord") => {
        if (type === "pakker") return `${apiUrl}/api/Option_MeetingPakke`;
        if (type === "udstyr") return `${apiUrl}/api/Option_MeetingUdstyr`;
        return `${apiUrl}/api/Option_MeetingBordopstilling`;
    };

    const openCreateOption = (type: "pakker" | "udstyr" | "bord") => {
        setOptionType(type);
        setOptionEditing(null);
        setOptionValue(0);
        setOptionText("");
        setOptionModalOpen(true);
    };

    const openEditOption = (type: "pakker" | "udstyr" | "bord", item: OptionDto) => {
        setOptionType(type);
        setOptionEditing(item);
        setOptionValue(item.value);
        setOptionText(item.text);
        setOptionModalOpen(true);
    };

    const saveOption = async () => {
        const url = getOptionUrl(optionType);
        const payload = {
            value: optionValue,
            text: optionText,
        };

        if (!optionText.trim()) {
            showNotification({ title: "Mangler", message: "Tekst er påkrævet" });
            return;
        }

        if (!optionEditing) {
            await axios.post(url, payload);
            showNotification({ title: "Gemt", message: "Oprettet" });
        } else {
            await axios.put(`${url}/${optionEditing.value}`, payload);
            showNotification({ title: "Gemt", message: "Opdateret" });
        }

        setOptionModalOpen(false);
        if (eventId) await refreshAll(eventId);
    };

    const deleteOption = async (type: "pakker" | "udstyr" | "bord", value: number) => {
        const url = getOptionUrl(type);
        await axios.delete(`${url}/${value}`);
        showNotification({ title: "OK", message: "Slettet" });

        if (eventId) await refreshAll(eventId);
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Meeting admin"
                titleDa="Møde admin"
                descriptionEn="Manage bookings, packages, equipment and table setup"
                descriptionDa="Administrer bookinger, pakker, udstyr og bordopstilling"
                imageSrc={Billede1}
            />

            <Container size="xl" py="xl">
                <Paper withBorder p="lg" radius="md">
                    <Group justify="space-between" align="flex-end">
                        <Stack gap={2}>
                            <Title order={3}>Møde Admin</Title>
                            <Text c="dimmed" size="sm">
                                Vælg event og administrer bookinger og opsætning
                            </Text>
                        </Stack>

                        <Select
                            label="Vælg event"
                            data={eventOptions}
                            value={eventId ? String(eventId) : null}
                            onChange={(v) => setEventId(v ? Number(v) : null)}
                            searchable
                            w={320}
                        />
                    </Group>
                </Paper>

                <Paper withBorder p="lg" radius="md" mt="md">
                    <Tabs value={activeTab} onChange={onTabChange} color="red">
                        <Tabs.List>
                            <Tabs.Tab value="bookings">Bookinger</Tabs.Tab>
                            <Tabs.Tab value="pakker">Pakker</Tabs.Tab>
                            <Tabs.Tab value="udstyr">Udstyr</Tabs.Tab>
                            <Tabs.Tab value="bord">Bordopstilling</Tabs.Tab>
                        </Tabs.List>

                        <Tabs.Panel value="bookings" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Bookinger</Text>
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    onClick={openCreateBooking}
                                    disabled={!eventId}
                                    style={{ backgroundColor: RED }}
                                    styles={{
                                        root: {
                                            transition: "transform 120ms ease, box-shadow 120ms ease",
                                            "&:hover": {
                                                transform: "translateY(-1px)",
                                                boxShadow: "0 10px 18px rgba(255,94,94,0.25)",
                                                backgroundColor: RED,
                                            },
                                        },
                                    }}
                                >
                                    Opret booking
                                </Button>
                            </Group>

                            <Table.ScrollContainer minWidth={1100}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Dato</Table.Th>
                                            <Table.Th>Tid</Table.Th>
                                            <Table.Th>Møde</Table.Th>
                                            <Table.Th>Kontakt</Table.Th>
                                            <Table.Th>Email</Table.Th>
                                            <Table.Th>Antal</Table.Th>
                                            <Table.Th>Pakke</Table.Th>
                                            <Table.Th>Bord</Table.Th>
                                            <Table.Th>Udstyr</Table.Th>
                                            <Table.Th />
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {bookings.map((b) => (
                                            <Table.Tr key={b.meetingId}>
                                                <Table.Td>{b.meetingDate}</Table.Td>
                                                <Table.Td>{b.meetingStart} - {b.meetingSlut}</Table.Td>
                                                <Table.Td>{b.meetingName}</Table.Td>
                                                <Table.Td>{b.kontaktPerson}</Table.Td>
                                                <Table.Td>{b.kontaktPersonEmail}</Table.Td>
                                                <Table.Td>{b.antalPersoner}</Table.Td>
                                                <Table.Td>
                                                    {b.meetingPakkeId != null
                                                        ? pakkeMap[b.meetingPakkeId] ?? b.meetingPakkeId
                                                        : "-"}
                                                </Table.Td>
                                                <Table.Td>
                                                    {b.bordOpstilling != null
                                                        ? bordMap[b.bordOpstilling] ?? b.bordOpstilling
                                                        : "-"}
                                                </Table.Td>
                                                <Table.Td>
                                                    {b.udstyr && b.udstyr.length > 0 ? (
                                                        <Group gap="xs">
                                                            {b.udstyr.map((id) => (
                                                                <Badge key={id} color="red" variant="light">
                                                                    {udstyrMap[id] ?? id}
                                                                </Badge>
                                                            ))}
                                                        </Group>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group justify="flex-end" gap="xs">
                                                        <Tooltip label="Rediger">
                                                            <ActionIcon variant="light" onClick={() => openEditBooking(b)}>
                                                                <IconPencil size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        <Tooltip label="Slet">
                                                            <ActionIcon color="red" variant="light" onClick={() => deleteBooking(b.meetingId)}>
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Tabs.Panel>

                        <Tabs.Panel value="pakker" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Pakker</Text>
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    onClick={() => openCreateOption("pakker")}
                                    style={{ backgroundColor: RED }}
                                >
                                    Opret pakke
                                </Button>
                            </Group>

                            <Table.ScrollContainer minWidth={700}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Value</Table.Th>
                                            <Table.Th>Tekst</Table.Th>
                                            <Table.Th />
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {pakker.map((p) => (
                                            <Table.Tr key={p.value}>
                                                <Table.Td>{p.value}</Table.Td>
                                                <Table.Td>{p.text}</Table.Td>
                                                <Table.Td>
                                                    <Group justify="flex-end" gap="xs">
                                                        <Tooltip label="Rediger">
                                                            <ActionIcon variant="light" onClick={() => openEditOption("pakker", p)}>
                                                                <IconPencil size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        <Tooltip label="Slet">
                                                            <ActionIcon color="red" variant="light" onClick={() => deleteOption("pakker", p.value)}>
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Tabs.Panel>

                        <Tabs.Panel value="udstyr" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Udstyr</Text>
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    onClick={() => openCreateOption("udstyr")}
                                    style={{ backgroundColor: RED }}
                                >
                                    Opret udstyr
                                </Button>
                            </Group>

                            <Table.ScrollContainer minWidth={700}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Value</Table.Th>
                                            <Table.Th>Tekst</Table.Th>
                                            <Table.Th />
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {udstyr.map((u) => (
                                            <Table.Tr key={u.value}>
                                                <Table.Td>{u.value}</Table.Td>
                                                <Table.Td>{u.text}</Table.Td>
                                                <Table.Td>
                                                    <Group justify="flex-end" gap="xs">
                                                        <Tooltip label="Rediger">
                                                            <ActionIcon variant="light" onClick={() => openEditOption("udstyr", u)}>
                                                                <IconPencil size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        <Tooltip label="Slet">
                                                            <ActionIcon color="red" variant="light" onClick={() => deleteOption("udstyr", u.value)}>
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Tabs.Panel>

                        <Tabs.Panel value="bord" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Bordopstilling</Text>
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    onClick={() => openCreateOption("bord")}
                                    style={{ backgroundColor: RED }}
                                >
                                    Opret bordopstilling
                                </Button>
                            </Group>

                            <Table.ScrollContainer minWidth={700}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Value</Table.Th>
                                            <Table.Th>Tekst</Table.Th>
                                            <Table.Th />
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {bordopstillinger.map((b) => (
                                            <Table.Tr key={b.value}>
                                                <Table.Td>{b.value}</Table.Td>
                                                <Table.Td>{b.text}</Table.Td>
                                                <Table.Td>
                                                    <Group justify="flex-end" gap="xs">
                                                        <Tooltip label="Rediger">
                                                            <ActionIcon variant="light" onClick={() => openEditOption("bord", b)}>
                                                                <IconPencil size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        <Tooltip label="Slet">
                                                            <ActionIcon color="red" variant="light" onClick={() => deleteOption("bord", b.value)}>
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Tabs.Panel>
                    </Tabs>
                </Paper>

                <Modal
                    opened={bookingModalOpen}
                    onClose={() => setBookingModalOpen(false)}
                    title={bookingEditing ? "Rediger booking" : "Opret booking"}
                    centered
                    size="lg"
                >
                    <Stack>
                        <TextInput label="Mødenavn" value={meetingName} onChange={(e) => setMeetingName(e.currentTarget.value)} />
                        <Group grow>
                            <TextInput label="Dato" type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.currentTarget.value)} />
                            <TextInput label="Start" type="time" value={meetingStart} onChange={(e) => setMeetingStart(e.currentTarget.value)} />
                            <TextInput label="Slut" type="time" value={meetingSlut} onChange={(e) => setMeetingSlut(e.currentTarget.value)} />
                        </Group>

                        <TextInput label="Kontaktperson" value={kontaktPerson} onChange={(e) => setKontaktPerson(e.currentTarget.value)} />
                        <Group grow>
                            <TextInput label="Mobil" value={kontaktPersonMobile} onChange={(e) => setKontaktPersonMobile(e.currentTarget.value)} />
                            <TextInput label="Email" value={kontaktPersonEmail} onChange={(e) => setKontaktPersonEmail(e.currentTarget.value)} />
                        </Group>

                        <NumberInput label="Antal personer" value={antalPersoner} onChange={(v) => setAntalPersoner(Number(v) || 0)} min={0} />

                        <Group grow>
                            <Select
                                label="Pakke"
                                data={pakkeOptions}
                                value={meetingPakkeId != null ? String(meetingPakkeId) : null}
                                onChange={(v) => setMeetingPakkeId(v ? Number(v) : null)}
                                clearable
                            />

                            <Select
                                label="Bordopstilling"
                                data={bordOptions}
                                value={bordOpstilling != null ? String(bordOpstilling) : null}
                                onChange={(v) => setBordOpstilling(v ? Number(v) : null)}
                                clearable
                            />
                        </Group>

                        <TextInput label="Udstyr" value={bookingUdstyr} onChange={(e) => setBookingUdstyr(e.currentTarget.value)} />

                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setBookingModalOpen(false)}>
                                Annullér
                            </Button>
                            <Button onClick={saveBooking}>Gem</Button>
                        </Group>
                    </Stack>
                </Modal>

                <Modal
                    opened={optionModalOpen}
                    onClose={() => setOptionModalOpen(false)}
                    title={optionEditing ? "Rediger" : "Opret"}
                    centered
                >
                    <Stack>
                        <NumberInput
                            label="Value"
                            value={optionValue}
                            onChange={(v) => setOptionValue(Number(v) || 0)}
                        />
                        <TextInput
                            label="Tekst"
                            value={optionText}
                            onChange={(e) => setOptionText(e.currentTarget.value)}
                        />

                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setOptionModalOpen(false)}>
                                Annullér
                            </Button>
                            <Button onClick={saveOption}>Gem</Button>
                        </Group>
                    </Stack>
                </Modal>
            </Container>
        </div>
    );
}